import { combineReducers } from 'redux';
import { call, debounce, put, select, takeLeading } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { PaymentBasic, PaymentListResponse } from 'generated/bffFinancialInfo/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { BillingReqeustFilter, PaymentsOverviewFilterParams } from 'models/Billing';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { Pagination } from 'models/Pagination';
import { pushAdobeAnalyticsEventFormSubmitAction } from 'saga/adobeAnalytics/ducks';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectBillingCaRefNo } from 'saga/billing/ducks';

/* STATE */
export interface PaymentsOverviewState {
  paymentList: PaymentBasic[];
  totalCount: number;
  pagination: Pagination;
  loadingStatus: LoadingStatus;
  filter: PaymentsOverviewFilterParams;
}

/* ACTION TYPES */
export enum PaymentsOverviewActionTypes {
  GetPaymentList = '@@PaymentsOverview/GET_PAYMENT_LIST',
  SetLoading = '@@PaymentsOverview/SET_PAYMENT_LOADING',
  SetFilter = '@@PaymentsOverview/SET_PAYMENT_OVERVIEW_FILTER',
  SetFilterDebounced = '@@PaymentsOverview/SET_PAYMENT_OVERVIEW_FILTER_DEBOUNCED',
  SetPage = '@@PaymentsOverview/SET_PAGE',
  ResetData = '@@PaymentsOverview/RESET_DATA',
}

/* ACTIONS */
export const getPaymentListActions = createApiActionCreators(PaymentsOverviewActionTypes.GetPaymentList);
export const setPaymentLoadingAction = createActionCreator(PaymentsOverviewActionTypes.SetLoading);
export const setPaymentOverViewFilterAction = createActionCreator(PaymentsOverviewActionTypes.SetFilter);
export const setPaymentOverViewFilterDebouncedAction = createActionCreator(
  PaymentsOverviewActionTypes.SetFilterDebounced
);
export const setPaymentOverViewPageAction = createActionCreator(PaymentsOverviewActionTypes.SetPage);
export const resetDataAction = createActionCreator(PaymentsOverviewActionTypes.ResetData);

/* REDUCERS */
const initialState: PaymentsOverviewState = {
  paymentList: [],
  totalCount: 0,
  pagination: {
    page: 1,
    pageSize: 20,
    forceCheck: false,
    initPages: 1,
  },
  loadingStatus: LoadingStatus.loading,
  filter: {},
};

const paymentList = createReducer(initialState.paymentList, {
  [PaymentsOverviewActionTypes.GetPaymentList]: {
    [RequestActionTypes.SUCCESS]: (state: PaymentBasic[], payload: PaymentListResponse & Pagination) => {
      if (!payload?.payments?.length) {
        return initialState.paymentList;
      }

      return payload.page === initialState.pagination.page ? payload.payments : [...state, ...payload.payments];
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
  [PaymentsOverviewActionTypes.ResetData]: () => [],
});

const totalCount = createReducer(initialState.totalCount, {
  [PaymentsOverviewActionTypes.GetPaymentList]: {
    [RequestActionTypes.SUCCESS]: (state: PaymentBasic[], payload: PaymentListResponse) =>
      (payload?.totalPages ?? 0) * 20,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const pagination = createReducer(initialState.pagination, {
  [PaymentsOverviewActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [PaymentsOverviewActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [PaymentsOverviewActionTypes.SetFilterDebounced]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
  [PaymentsOverviewActionTypes.GetPaymentList]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
  [PaymentsOverviewActionTypes.ResetData]: (state: Pagination) => ({
    ...state,
    page: initialState.pagination.page,
  }),
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [PaymentsOverviewActionTypes.GetPaymentList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [PaymentsOverviewActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const filter = createReducer(initialState.filter, {
  [PaymentsOverviewActionTypes.SetFilter]: (
    state: PaymentsOverviewFilterParams,
    payload: Partial<PaymentsOverviewFilterParams>
  ) => ({
    ...state,
    ...payload,
  }),
  [PaymentsOverviewActionTypes.SetFilterDebounced]: (
    state: PaymentsOverviewFilterParams,
    payload: Partial<PaymentsOverviewFilterParams>
  ) => ({
    ...state,
    ...payload,
  }),
});

export default combineReducers<PaymentsOverviewState>({
  paymentList,
  totalCount,
  pagination,
  loadingStatus,
  filter,
});

/* SELECTORS*/
const selectPaymentsOverview = (state: AppState) => state.paymentsOverview;

export const selectPaymentsOverviewLoading = (state: AppState): LoadingStatus =>
  selectPaymentsOverview(state).loadingStatus;

export const selectPaymentsOverviewPagination = (state: AppState): Pagination =>
  selectPaymentsOverview(state).pagination;

export const selectPaymentList = (state: AppState): PaymentBasic[] => selectPaymentsOverview(state).paymentList;

export const selectPaymentListTotalCount = (state: AppState): number => selectPaymentsOverview(state).totalCount;

export const selectPaymentOverviewFilter = (state: AppState): PaymentsOverviewFilterParams =>
  selectPaymentsOverview(state).filter;

/* SAGAS */
function* getPaymentList() {
  yield put(setPaymentLoadingAction(LoadingStatus.loading));

  const customer: CustomerInfo = yield select(selectContextCustomerInfo);
  const requestFilter: Array<BillingReqeustFilter> = [
    {
      column: 'cuRefNo',
      compareType: 'EQUAL',
      value: customer?.cuRefNo,
    },
  ];

  const selectedCaRefNo: string = yield select(selectBillingCaRefNo);

  if (selectedCaRefNo) {
    requestFilter.push({
      column: 'caRefNo',
      compareType: 'EQUAL',
      value: selectedCaRefNo,
    });
  }

  const paymentOverviewFilter: PaymentsOverviewFilterParams = yield select(selectPaymentOverviewFilter);
  const paymentsOverviewStoredFilter = JSON.parse(String(localStorage.getItem('paymentsOverviewFilter')));
  const variableSymbol = paymentOverviewFilter.variableSymbol || paymentsOverviewStoredFilter?.variableSymbol;
  const paymentType = paymentOverviewFilter.paymentType || paymentsOverviewStoredFilter?.paymentType;

  if (variableSymbol) {
    requestFilter.push({
      column: 'variableSymbol',
      compareType: 'EQUAL',
      value: variableSymbol,
    });
  }

  if (paymentType) {
    requestFilter.push({
      column: 'paymentType',
      compareType: 'EQUAL',
      value: paymentType,
    });
  }

  const pagination: Pagination = yield select(selectPaymentsOverviewPagination);

  const request: ApiClientRequest = {
    apiCall: call(
      api.getPaymentList,
      yield getApiUrl(environment.urlConfig.financialInfo),
      JSON.stringify(requestFilter),
      pagination.page
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getPaymentListActions.success({
        ...resp.data,
        ...pagination,
      })
    );
  } else {
    yield put(getPaymentListActions.failure());
  }
}

function* getPaymentListWithAdobeAction() {
  yield call(pushAdobeAnalyticsEventFormSubmit);
  yield call(getPaymentList);
}

function* pushAdobeAnalyticsEventFormSubmit() {
  yield put(pushAdobeAnalyticsEventFormSubmitAction('2-2-1b-filterPaymentsList'));
}

/* EXPORT */
export function* paymentsOverviewSaga() {
  yield takeLeading(
    [
      createActionType(PaymentsOverviewActionTypes.GetPaymentList, RequestActionTypes.REQUEST),
      PaymentsOverviewActionTypes.SetFilter,
      PaymentsOverviewActionTypes.SetPage,
    ],
    getPaymentList
  );

  yield takeLeading(PaymentsOverviewActionTypes.SetFilter, pushAdobeAnalyticsEventFormSubmit);

  yield debounce(1000, PaymentsOverviewActionTypes.SetFilterDebounced, getPaymentListWithAdobeAction);
}
